import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'components/AuthGuard';
import RoleGuard from 'components/RoleGuard';

// render - color
const Color = Loadable(lazy(() => import('pages/component-overview/color')));
const Typography = Loadable(lazy(() => import('pages/component-overview/typography')));
const Shadow = Loadable(lazy(() => import('pages/component-overview/shadows')));

// render - sample page
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));

// render - ERP pages
const ItemsList = Loadable(lazy(() => import('pages/erp/items/ItemsPage')));
const ItemDetail = Loadable(lazy(() => import('pages/erp/items/ItemDetailPage')));
const ItemCategories = Loadable(lazy(() => import('pages/erp/ItemCategories')));
const ErpQuickStart = Loadable(lazy(() => import('pages/erp/ErpQuickStart')));
const WarehousesPage = Loadable(lazy(() => import('pages/erp/Warehouses')));
const InventoryMovesList = Loadable(lazy(() => import('pages/erp/InventoryMovesList')));
const InventoryMoveNew = Loadable(lazy(() => import('pages/erp/InventoryMoveNew')));
const RoutingsList = Loadable(lazy(() => import('pages/erp/RoutingsList')));
const RoutingDetail = Loadable(lazy(() => import('pages/erp/RoutingDetail')));
const CustomerOrders = Loadable(lazy(() => import('pages/erp/CustomerOrders')));
const CustomerOrdersImport = Loadable(lazy(() => import('pages/erp/CustomerOrdersImport')));
const WorkOrdersList = Loadable(lazy(() => import('pages/erp/WorkOrdersList')));
const WorkOrderNew = Loadable(lazy(() => import('pages/erp/WorkOrderNew')));
const WorkOrderOperations = Loadable(lazy(() => import('pages/erp/WorkOrderOperations')));
const StatusBoard = Loadable(lazy(() => import('pages/erp/StatusBoard')));
const PurchasingPage = Loadable(lazy(() => import('pages/erp/PurchasingPage')));
const OperationsPage = Loadable(lazy(() => import('pages/erp/Operations')));
const WorkcentersPage = Loadable(lazy(() => import('pages/erp/Workcenters')));
const UserManagement = Loadable(lazy(() => import('pages/erp/UserManagement')));
const ShopFloor = Loadable(lazy(() => import('pages/erp/production/ShopFloor')));
const PartsBoard = Loadable(lazy(() => import('pages/erp/production/PartsBoard')));
const AircraftProgressBoard = Loadable(lazy(() => import('pages/erp/production/AircraftProgressBoard')));

const AuthenticatedLayout = () => (
  <AuthGuard>
    <DashboardLayout />
  </AuthGuard>
);

const withRole = (element, roles) => <RoleGuard roles={roles}>{element}</RoleGuard>;

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <AuthenticatedLayout />,
  children: [
    {
      path: '/',
      element: <ErpQuickStart />
    },
    {
      path: 'dashboard',
      children: [
        {
          path: 'default',
          element: <ErpQuickStart />
        }
      ]
    },
    // ERP Routes
    {
      path: 'erp',
      children: [
        {
          path: 'quick-start',
          element: withRole(<ErpQuickStart />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'items',
          element: withRole(<ItemsList />, ['manager'])
        },
        {
          path: 'items/:id',
          element: withRole(<ItemDetail />, ['manager'])
        },
        {
          path: 'item-categories',
          element: withRole(<ItemCategories />, ['manager'])
        },
        {
          path: 'warehouses',
          element: withRole(<WarehousesPage />, ['manager'])
        },
        {
          path: 'warehouses/:id',
          element: withRole(<WarehousesPage />, ['manager'])
        },
        {
          path: 'locations',
          element: withRole(<Navigate to="/erp/warehouses" replace />, ['manager'])
        },
        {
          path: 'inventory-moves',
          element: withRole(<InventoryMovesList />, ['manager', 'planner'])
        },
        {
          path: 'status-board',
          element: withRole(<StatusBoard />, ['manager', 'planner'])
        },
        {
          path: 'inventory-moves/new',
          element: withRole(<InventoryMoveNew />, ['manager', 'planner'])
        },
        {
          path: 'inventory/balances',
          element: withRole(<Navigate to="/erp/inventory-moves" replace />, ['manager', 'planner'])
        },
        {
          path: 'lots',
          element: withRole(<Navigate to="/erp/inventory-moves" replace />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'routings',
          element: withRole(<RoutingsList />, ['manager'])
        },
        {
          path: 'routings/:id',
          element: withRole(<RoutingDetail />, ['manager'])
        },
        {
          path: 'scheduling',
          element: withRole(<Navigate to="/erp/work-orders" replace />, ['manager', 'planner'])
        },
        {
          path: 'production',
          element: withRole(<Navigate to="/erp/work-orders" replace />, ['manager', 'planner'])
        },
        {
          path: 'quality',
          element: withRole(<Navigate to="/erp/shop-floor" replace />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'external-operations',
          element: withRole(<Navigate to="/erp/quick-start" replace />, ['manager', 'planner'])
        },
        {
          path: 'purchasing',
          element: withRole(<PurchasingPage />, ['manager', 'planner'])
        },
        {
          path: 'finance',
          element: withRole(<Navigate to="/erp/quick-start" replace />, ['manager'])
        },
        {
          path: 'operations',
          element: withRole(<OperationsPage />, ['manager'])
        },
        {
          path: 'workcenters',
          element: withRole(<WorkcentersPage />, ['manager'])
        },
        {
          path: 'users',
          element: withRole(<UserManagement />, ['manager'])
        },
        {
          path: 'customer-orders',
          element: withRole(<CustomerOrders />, ['manager', 'planner'])
        },
        {
          path: 'customer-orders/import',
          element: withRole(<CustomerOrdersImport />, ['manager', 'planner'])
        },
        {
          path: 'work-orders',
          element: withRole(<WorkOrdersList />, ['manager', 'planner'])
        },
        {
          path: 'work-order-approvals',
          element: withRole(<Navigate to="/erp/work-orders" replace />, ['manager', 'planner'])
        },
        {
          path: 'work-orders/new',
          element: withRole(<WorkOrderNew />, ['manager', 'planner'])
        },
        {
          path: 'work-orders/:id/operations',
          element: withRole(<WorkOrderOperations />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'shop-floor',
          element: withRole(<ShopFloor />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'production/parts-board',
          element: withRole(<PartsBoard />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'production/aircraft-progress',
          element: withRole(<AircraftProgressBoard />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'configurator',
          element: withRole(<Navigate to="/erp/work-orders" replace />, ['manager', 'planner', 'operator'])
        },
        {
          path: 'maintenance',
          element: withRole(<Navigate to="/erp/quick-start" replace />, ['manager'])
        }
      ]
    },
    {
      path: 'typography',
      element: <Typography />
    },
    {
      path: 'color',
      element: <Color />
    },
    {
      path: 'shadow',
      element: <Shadow />
    },
    {
      path: 'sample-page',
      element: <SamplePage />
    }
  ]
};

export default MainRoutes;
